import { type CSSObject, createTheme } from "@mui/material/styles";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    blue: true;
    green: true;
    close: true;
    brown: true;
    darkBlue: true;
  }
}

const createButtonStyle = ({
  borderColor,
  backgroundColor,
  textColor,
  insetShadow,
}: {
  borderColor: string;
  backgroundColor: string;
  textColor: string;
  insetShadow: string;
}) => ({
  border: `2px solid ${borderColor}`,
  backgroundColor,
  padding: "0 10px",
  color: textColor,
  fontSize: "14px",
  minWidth: "fit-content",
  fontWeight: 900,
  width: "fit-content",
  borderRadius: "18px",
  textTransform: "none",
  boxShadow: `inset 0 -8px 2px -5px ${insetShadow}, 0 3px 3px -1px #00000060`,
});

declare module "@mui/material/styles" {
  interface SimplePaletteColorOptions {
    yellow?: string;
    unbuilt?: string;
    bgBlock?: string;
    building?: string;
    darkBorder?: string;
    borderBlue?: string;
    bgCardQuest?: string;
    borderBrawn?: string;
    borderBlock?: string;
    lightYellow?: string;
    yellowBorder?: string;
    bgTabsQuests?: string;
    bgModalBuild?: string;
    bgBlockMenu?: string;
    bgDialogBuild?: string;
    borderSideBar?: string;
    disabledBlock?: string;
    textBlueColor?: string;
    moveModeBorder?: string;
    borderDarkBlue?: string;
    bgQuestionPage?: string;
    bgBuildingTitle?: string;
    selectedUnbuilt?: string;
    borderLightBlue?: string;
    borderCardQuest?: string;
    selectMoveBlock?: string;
    textResearchLab?: string;
    headerTabsQuests?: string;
    borderInfoDialog?: string;
    bgQuestionButton?: string;
    selectedBuilding?: string;
    borderModalBuild?: string;
    borderTabsQuests?: string;
    borderSelectBuild?: string;
    bgBlockMenuBorder?: string;
    textLightBlueColor?: string;
    selectedUnbuiltBorder?: string;
  }
}
export const theme = createTheme({
  typography: {
    fontSize: 14,
    fontFamily: `"Red Hat Display", sans-serif`,
    fontWeightRegular: 900,
  },

  components: {
    MuiButton: {
      variants: [
        {
          props: { variant: "brown" },
          style: createButtonStyle({
            borderColor: "#64390C",
            backgroundColor: "#FDC52C",
            textColor: "#64390C",
            insetShadow: "#c3920b",
          }) as CSSObject,
        },
        {
          props: { variant: "blue" },
          style: createButtonStyle({
            borderColor: "#223F88",
            backgroundColor: "#3BB1F3",
            textColor: "white",
            insetShadow: "#0C81C5",
          }) as CSSObject,
        },
        {
          props: { variant: "green" },
          style: createButtonStyle({
            borderColor: "#15502A",
            backgroundColor: "#97FC7F",
            textColor: "#15502A",
            insetShadow: "#5EBD5C",
          }) as CSSObject,
        },
        {
          props: { variant: "darkBlue" },
          style: createButtonStyle({
            borderColor: "#223F88",
            backgroundColor: "#497EE4",
            textColor: "white",
            insetShadow: "#2955AC",
          }) as CSSObject,
        },
        {
          props: { variant: "close" },
          style: createButtonStyle({
            borderColor: "#660D27",
            backgroundColor: "#E84B79",
            textColor: "white",
            insetShadow: "#BC355C",
          }) as CSSObject,
        },
      ],
    },
  },
  palette: {
    primary: {
      dark: "#233559",
      main: "#6081bf",
      light: "#829ED3",
      yellow: "#FDC52C",
      bgBlock: "#CBDDFF",
      contrastText: "#fff",
      darkBorder: "#1A2D50",
      borderBlue: "#88AAEA",
      bgBlockMenu: "#E4EDFF",
      lightYellow: "#FFDE85",
      yellowBorder: "#9C720E",
      bgModalBuild: "#E4EDFF",
      textBlueColor: "#1A2945",
      borderSideBar: "#9ebffb",
      borderDarkBlue: "#223F88",
      moveModeBorder: "#64390C",
      borderLightBlue: "#C2E7FF",
      textResearchLab: "#344871",
      selectMoveBlock: "#ffd86b",
      bgQuestionButton: "#3BB1F3",
      borderModalBuild: "#B2CBFC",
      bgBuildingTitle: "#15284E80",
      bgBlockMenuBorder: "#B2CBFC",
      borderSelectBuild: "#3F609E",
      textLightBlueColor: "#3BB1F3",
    },
    secondary: {
      main: "#EFE5DC",
      dark: "#d3c8bf",
      light: "#EFE5DC",
      unbuilt: "#EBDDCE",
      building: "#CBDDFF",
      contrastText: "#000",
      borderBlock: "#84A4DF",
      borderBrawn: "#64390C",
      bgCardQuest: "#CADEFF",
      bgTabsQuests: "#e1d5cb",
      bgDialogBuild: "#BDD0F2",
      bgQuestionPage: "#efe5dc",
      borderCardQuest: "#4B62A4",
      selectedUnbuilt: "#FFF6ED",
      selectedBuilding: "#e4eeff",
      borderInfoDialog: "#0B1E4D",
      headerTabsQuests: "#fbefe5",
      borderTabsQuests: "#C6B5A7",
      selectedUnbuiltBorder: "#C0D6FF",
      disabledBlock: "rgb(235, 221, 206, 0.2)",
    },
  },
});
